import { useEffect, useRef, useSyncExternalStore } from 'react';
import { create } from 'zustand';

import { designService } from '@paragon/data-connections';

// A canary report will be sent if a scroll target is requested but not activated within this time.
const CANARY_TIMEOUT = 1000;

export type ScrollTarget = 'selectedItemProperties' | 'cutPlaneButton';
type ScrollStoreState = { target: ScrollTarget; requestedAt: number } | null;

const scrollStore = create<ScrollStoreState>(() => null);

const getScrollTarget = () => scrollStore.getState();

const resetScrollTarget = (): void => void (scrollStore.getState() && scrollStore.setState(null, true));

/**
 * Request that the specified `target` be scrolled into view.
 *
 * Calling this method does not guarantee that the item will be scrolled into view. Specifically:
 *
 * 1. If the component is not rendered, scrolling will not begin until it is rendered.
 * 2. If another call to `scrollTo` sets a new scroll target before scrolling begins, the component will not scroll into view.
 * 3. User interactions (such as scrolling the page) can cancel or overwrite programmatic scrolling.
 *
 * @param target - The `ScrollTarget` to scroll to.
 */
export const scrollTo = (target: ScrollTarget) => scrollStore.setState({ target, requestedAt: Date.now() });

const scrollDelayBirdcageMonitor = (requestedScroll: ScrollStoreState) => () => {
  if (requestedScroll) {
    const elapsed = Date.now() - requestedScroll.requestedAt;
    if (elapsed > CANARY_TIMEOUT) {
      designService.canary.sendReport(`${CANARY_TIMEOUT}ms elapsed before scroll target was activated.`, undefined, {
        scrollTarget: requestedScroll.target,
        elapsedMs: elapsed.toString(),
      });
    }
  }
};

/**
 * Create a ref that will be scrolled to anytime the passed target is the active scroll target in state.
 *
 * Specifically, scrolling is triggered on the rising edge; that is, when `boundTarget` is first requested via `scrollTo`.
 *
 * @param boundTarget - A value to control when to scroll.
 * @returns A ref to attach to the scroll target.
 */
export const useScrollTo = (boundTarget: ScrollTarget) => {
  const ref = useRef<HTMLElement>(null);
  const requestedScroll = useSyncExternalStore(scrollStore.subscribe, getScrollTarget);

  useEffect(() => {
    const shouldScrollTo = requestedScroll?.target === boundTarget;
    if (shouldScrollTo) {
      resetScrollTarget();
      setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth' }), 500);
    }
    return scrollDelayBirdcageMonitor(requestedScroll);
  }, [requestedScroll, boundTarget]);

  return ref;
};
